/**
 * IndexedDB Transaction Helpers
 * Provides standardized utilities for transaction handling
 */

import { getConnection } from './dbConnection'

/**
 * Execute a database operation with proper transaction handling
 * @param {string|string[]} storeNames - The object store name(s)
 * @param {string} mode - The transaction mode ('readonly' or 'readwrite')
 * @param {Function} operation - Function that receives the store and performs operations
 * @param {Object} options - Additional options
 * @returns {Promise<any>} Result of the operation
 */
export const executeTransaction = async (
    storeNames,
    mode,
    operation,
    options = {},
) => {
    const {
        timeout = 10000, // 10 second default timeout
        retries = 0,
        retryDelay = 500,
    } = options

    let currentTry = 0
    let lastError = null

    while (currentTry <= retries) {
        try {
            const db = await getConnection()

            // Create a timer for timeout
            let timeoutId
            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = setTimeout(() => {
                    reject(new Error(`Transaction timeout after ${timeout}ms`))
                }, timeout)
            })

            // Execute the transaction
            const transactionPromise = new Promise((resolve, reject) => {
                try {
                    const transaction = db.transaction(storeNames, mode)

                    // If single store name is provided, pass the store directly
                    const storeOrStores = Array.isArray(storeNames)
                        ? storeNames.reduce((acc, name) => {
                              acc[name] = transaction.objectStore(name)
                              return acc
                          }, {})
                        : transaction.objectStore(storeNames)

                    // Run the operation with store(s) and transaction
                    const result = operation(storeOrStores, transaction)

                    transaction.oncomplete = () => {
                        resolve(result)
                    }

                    transaction.onerror = event => {
                        reject(
                            event.target.error ||
                                new Error('Transaction failed'),
                        )
                    }

                    transaction.onabort = event => {
                        reject(
                            event.target.error ||
                                new Error('Transaction aborted'),
                        )
                    }
                } catch (error) {
                    reject(error)
                }
            })

            // Wait for either transaction completion or timeout
            const result = await Promise.race([
                transactionPromise,
                timeoutPromise,
            ])
            clearTimeout(timeoutId)
            return result
        } catch (error) {
            lastError = error
            console.warn(`Transaction attempt ${currentTry + 1} failed:`, error)

            // Check if we should retry
            if (currentTry < retries) {
                await new Promise(resolve =>
                    setTimeout(resolve, retryDelay * Math.pow(2, currentTry)),
                )
                currentTry++
            } else {
                throw lastError
            }
        }
    }

    throw lastError
}

/**
 * Get all items from a store
 * @param {string} storeName - The object store name
 * @param {Object} options - Operation options
 * @returns {Promise<Array>} All items in the store
 */
export const getAll = async (storeName, options = {}) => {
    return executeTransaction(
        storeName,
        'readonly',
        store => {
            return new Promise(resolve => {
                const request = store.getAll()
                request.onsuccess = () => resolve(request.result || [])
            })
        },
        options,
    )
}

/**
 * Get an item by its key
 * @param {string} storeName - The object store name
 * @param {any} key - The item key
 * @param {Object} options - Operation options
 * @returns {Promise<any>} The item or null if not found
 */
export const getByKey = async (storeName, key, options = {}) => {
    return executeTransaction(
        storeName,
        'readonly',
        store => {
            return new Promise(resolve => {
                const request = store.get(key)
                request.onsuccess = () => resolve(request.result || null)
            })
        },
        options,
    )
}

/**
 * Put an item in a store
 * @param {string} storeName - The object store name
 * @param {Object} item - The item to store
 * @param {Object} options - Operation options
 * @returns {Promise<any>} The key of the stored item
 */
export const put = async (storeName, item, options = {}) => {
    return executeTransaction(
        storeName,
        'readwrite',
        store => {
            return new Promise(resolve => {
                const request = store.put(item)
                request.onsuccess = () => resolve(request.result)
            })
        },
        options,
    )
}

/**
 * Add an item to a store (fails if key exists)
 * @param {string} storeName - The object store name
 * @param {Object} item - The item to store
 * @param {Object} options - Operation options
 * @returns {Promise<any>} The key of the added item
 */
export const add = async (storeName, item, options = {}) => {
    return executeTransaction(
        storeName,
        'readwrite',
        store => {
            return new Promise((resolve, reject) => {
                const request = store.add(item)
                request.onsuccess = () => resolve(request.result)
                request.onerror = event => reject(event.target.error)
            })
        },
        options,
    )
}

/**
 * Delete an item from a store
 * @param {string} storeName - The object store name
 * @param {any} key - The key to delete
 * @param {Object} options - Operation options
 * @returns {Promise<void>}
 */
export const deleteItem = async (storeName, key, options = {}) => {
    return executeTransaction(
        storeName,
        'readwrite',
        store => {
            return new Promise(resolve => {
                const request = store.delete(key)
                request.onsuccess = () => resolve()
            })
        },
        options,
    )
}

/**
 * Clear a store
 * @param {string} storeName - The object store name
 * @param {Object} options - Operation options
 * @returns {Promise<void>}
 */
export const clear = async (storeName, options = {}) => {
    return executeTransaction(
        storeName,
        'readwrite',
        store => {
            return new Promise(resolve => {
                const request = store.clear()
                request.onsuccess = () => resolve()
            })
        },
        options,
    )
}

/**
 * Update an existing item in the store
 * @param {string} storeName - The object store name
 * @param {any} key - The key of the item to update
 * @param {Function} updateFn - Function that receives the current value and returns the updated value
 * @param {Object} options - Operation options
 * @returns {Promise<any>} The key of the updated item
 */
export const update = async (storeName, key, updateFn, options = {}) => {
    return executeTransaction(
        storeName,
        'readwrite',
        async store => {
            return new Promise((resolve, reject) => {
                const getRequest = store.get(key)

                getRequest.onsuccess = () => {
                    const value = getRequest.result
                    if (!value) {
                        reject(
                            new Error(
                                `Item with key ${key} not found in ${storeName}`,
                            ),
                        )
                        return
                    }

                    const updatedValue = updateFn(value)
                    const putRequest = store.put(updatedValue)

                    putRequest.onsuccess = () => resolve(putRequest.result)
                    putRequest.onerror = event => reject(event.target.error)
                }

                getRequest.onerror = event => reject(event.target.error)
            })
        },
        options,
    )
}
